<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Purchase',
  },
})

const { can } = useAbility()

const search       = ref('')
const filterStatus = ref(null)
const page         = ref(1)
const perPage      = ref(15)
const loading      = ref(false)
const receivings   = ref([])
const totalItems   = ref(0)

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Draft', value: 'draft' },
  { title: 'Dikonfirmasi', value: 'confirmed' },
]

const headers = [
  { title: 'No. Receiving', key: 'receiving_number', sortable: false },
  { title: 'No. PO', key: 'po_number', sortable: false },
  { title: 'Supplier', key: 'supplier', sortable: false },
  { title: 'Tgl. Terima', key: 'received_at', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 80 },
]

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'

const fetchReceivings = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value)       query.search = search.value
    if (filterStatus.value) query.status = filterStatus.value

    const response = await useApi('/api/v1/purchase-receivings', { query }).get()
    const body = response?.data ?? response

    receivings.value = body?.data ?? []
    totalItems.value = body?.meta?.total ?? 0
  } catch (err) {
    console.error('[PurchaseReceivings] fetch error', err)
    receivings.value = []
  } finally {
    loading.value = false
  }
}

watch([search, filterStatus, page], fetchReceivings, { immediate: true })
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardText class="d-flex flex-wrap gap-4 align-center justify-space-between">
          <div class="d-flex gap-3 flex-wrap align-center">
            <VTextField
              v-model="search"
              placeholder="Cari no. receiving..."
              prepend-inner-icon="ri-search-line"
              density="compact"
              hide-details
              clearable
              style="min-width: 210px"
            />
            <VSelect
              v-model="filterStatus"
              :items="statusOptions"
              label="Status"
              density="compact"
              hide-details
              style="min-width: 160px"
            />
          </div>

          <VBtn
            v-if="can('create', 'Purchase')"
            :to="{ name: 'purchase-receivings-create' }"
            prepend-icon="ri-add-line"
          >
            Buat Receiving
          </VBtn>
        </VCardText>

        <VDivider />

        <VDataTableServer
          v-model:page="page"
          :headers="headers"
          :items="receivings"
          :items-length="totalItems"
          :items-per-page="perPage"
          :loading="loading"
          no-data-text="Belum ada receiving."
        >
          <template #item.po_number="{ item }">
            {{ item.purchase_order?.po_number ?? '-' }}
          </template>

          <template #item.supplier="{ item }">
            {{ item.purchase_order?.supplier ?? '-' }}
          </template>

          <template #item.received_at="{ item }">
            {{ formatDate(item.received_at) }}
          </template>

          <template #item.status="{ item }">
            <VChip
              :color="item.status === 'confirmed' ? 'success' : 'default'"
              size="small"
            >
              {{ item.status === 'confirmed' ? 'Dikonfirmasi' : 'Draft' }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <IconBtn :to="{ name: 'purchase-receivings-id', params: { id: item.id } }">
              <VIcon icon="ri-eye-line" />
              <VTooltip activator="parent">Detail</VTooltip>
            </IconBtn>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>
</template>
